import { createReducer } from "reduxsauce"
import { takeLatest, select, put } from "redux-saga/effects"
import { filter, forEach, isNumber, omit, get, some } from "lodash"
import createActions from "helpers/actionCreator"
import { Types as LoaderTypes } from "model/data"
import { Types as PortfolioRootTypes } from "modules/portfolio-root/model"
import { Creators as notificationCreators } from "modules/notifications/model"
import { getUserPreference } from "helpers/userPreferences"
import { handleSetSingleDate } from "helpers/date-preferences"
import { getExposurePreferencesItems, resetPreferencesItems, getConstraintsPreferencesItems, getForecastPreferencesItems } from "./helpers"
import { getForecasts } from "./selectors"
import { getCurrentSettings, getSelectedCustomForecast, getForecastConstraint } from "modules/optimization/selectors/constraints"

export const { Types, Creators } = createActions("ompnt/modules/optimization/", {
  setRiskTarget: ["riskTarget"],
  setConstraints: {
    props: ["constraintsType", "constraints"],
    updatePreference: {
      getItems: (state, { constraintsType }) => getConstraintsPreferencesItems(state, constraintsType),
    },
    track: true,
    trackType: "factor-target-selection",
  },
  setSettings: {
    props: ["settings"],
    updatePreference: {
      getItems: (state) => getForecastPreferencesItems(state),
    },
  },
  removeFactor: {
    props: ["factorId", "contentSetId"],
    updatePreference: {
      getItems: (state) => getExposurePreferencesItems(state),
    },
  },
  toggleTradesPanel: [],
  resetSettings: {
    props: [],
    updatePreference: {
      actionType: "delete",
      getItems: (state) => [...resetPreferencesItems(state)],
    },
  },
  setConstraint: ["id", "value"],
  setSetting: ["id", "value"],
  removeActivatedFactor: ["factorId", "contentSetId"],
  setFactors: ["factorId", "contentSetId", "min", "max", "exposureWeighting"],
  loadOptimizationConfig: ["experiment"],
  loadSecuritiesConstraints: ["securities"],
  loadExperiment: ["experiment"],
  reloadConstraints: [],
  setOptimizationDate: ["date"],
})

const DEFAULT_STATE = {
  constraintsStoreKey: "",
  portfolioType: null,
  tradesPanelOpen: false,
  saveAndCompareEnabled: false,
  experiment: null,
  riskTarget: {},
  preferencesConstraints: {},
  constraints: {
    settings: {
      horizon: 252,
    },
    general: {
      tradeSwaps: true,
    },
    exposure: [],
    securities: {},
  },
  activatedConstraints: {
    settings: {},
    general: {},
    exposure: {},
  },
}

function handleLoadSuccess(state, action) {
  if (!state.experiment) {
    if (action.resource === "portfolio") {
      return { ...state, ...DEFAULT_STATE, portfolioType: action.data.type }
    }

    if (action.resource === "optimizationConstraintsPreferences") {
      const storedConstraints = getUserPreference(action.data, "optimizationConstraints")
      const constraints = {
        settings: {
          horizon: 252,
        },
        general: {
          tradeSwaps: true,
        },
        exposure: [],
        securities: {},
      }

      if (storedConstraints) {
        forEach(storedConstraints, (value, id) => {
          if (state.portfolioType === "LONG_SHORT" || (state.portfolioType !== "LONG_SHORT" && id !== "netLong")) {
            constraints.general[id] = isNumber(value) ? parseFloat(value) : value
          }
        })
      }

      const storedForecastSettings = getUserPreference(action.data, "optimizationForecast")
      if (storedForecastSettings) {
        forEach(storedForecastSettings, (value, id) => {
          constraints.settings[id] = isNumber(value) ? parseInt(value, 10) : value
        })
      }

      constraints.exposure = getUserPreference(action.data, "optimizationExposures", [])

      return { ...state, ...DEFAULT_STATE, constraints, preferredConstraints: constraints }
    }
  }

  return state
}

function handleSetRiskTarget(state, { riskTarget }) {
  return {
    ...state,
    riskTarget: {
      predictedTotalRisk: riskTarget.x,
    },
  }
}

function handleToggleTradesPanel(state) {
  return { ...state, tradesPanelOpen: !state.tradesPanelOpen }
}

function handleSetConstraints(state, { constraintsType, constraints }) {
  const newConstraints = { ...state.constraints, [constraintsType]: constraints }

  return {
    ...state,
    constraints: newConstraints,
    preferredConstraints: state.experiment ? state.preferredConstraints : newConstraints,
    riskTarget: {},
  }
}

function handleSetSettings(state, { settings }) {
  const newConstraints = { ...state.constraints, settings }

  return {
    ...state,
    constraints: newConstraints,
    preferredConstraints: state.experiment ? state.preferredConstraints : newConstraints,
    riskTarget: {},
  }
}

function handleRemoveFactor(state, { factorId, contentSetId }) {
  const newConstraints = { ...state.constraints, exposure: filter(state.constraints.exposure, factor => !(factor.id === factorId && factor.contentSetId === contentSetId)) }

  return {
    ...state,
    constraints: newConstraints,
    preferredConstraints: state.experiment ? state.preferredConstraints : newConstraints,
    riskTarget: {},
  }
}

function handleResetSettings(state) {
  const newConstraints = {
    settings: {
      horizon: 252,
    },
    general: {
      tradeSwaps: true,
    },
    exposure: [],
    securities: {},
  }

  return {
    ...state,
    constraints: newConstraints,
    preferredConstraints: state.experiment ? state.preferredConstraints : newConstraints,
    activatedConstraints: {
      settings: {},
      general: {},
      exposure: {},
    },
  }
}

function handleSetConstraint(state, { id, value }) {
  // [TODO] use immutability-helper
  return {
    ...state,
    activatedConstraints: {
      ...state.activatedConstraints,
      general: {
        ...state.activatedConstraints.general,
        [id]: { value },
      },
    },
  }
}

function handleSetSetting(state, { id, value }) {
  return {
    ...state,
    activatedConstraints: {
      ...state.activatedConstraints,
      settings: {
        ...state.activatedConstraints.settings,
        [id]: { value },
      },
    },
  }
}

function handleSetFactors(state, { factorId, contentSetId, min, max, exposureWeighting }) {
  const id = contentSetId ? `${contentSetId}.${factorId}` : factorId
  return {
    ...state,
    activatedConstraints: {
      ...state.activatedConstraints,
      exposure: {
        ...state.activatedConstraints.exposure,
        [id]: {
          min,
          max,
          factorId,
          contentSetId,
          exposureWeighting,
        },
      },
    },
  }
}

function handleRemoveActivatedFactor(state, { factorId, contentSetId }) {
  const id = contentSetId ? `${contentSetId}.${factorId}` : factorId
  return {
    ...state,
    activatedConstraints: {
      ...state.activatedConstraints,
      exposure: omit(state.activatedConstraints.exposure, id),
    },
  }
}

function handleLoadSecuritiesConstraints(state, { securities }) {
  return { ...state, constraints: { ...state.constraints, securities } }
}

function handleReloadConstraints(state) {
  return {
    ...state,
    experiment: null,
    riskTarget: {},
    activatedConstraints: {
      settings: {},
      general: {},
      exposure: {},
    },
    constraints: state.preferredConstraints,
  }
}

function handleLoadOptimizationConfig(state, { experiment }) {
  const optimizationConfig = get(experiment, "config")
  if (!optimizationConfig) {
    return state
  }

  return {
    ...state,
    experiment,
    constraints: { ...state.constraints, ...optimizationConfig.constraints },
    activatedConstraints: {
      settings: {},
      general: {},
      exposure: {},
    },
    riskTarget: { ...state.riskTarget, ...optimizationConfig.riskTarget },
  }
}

function handleSetOptimizationDate(state, { date }) {
  return { ...state, optimizationDate: date }
}

function handlePortfolioSetPeriod(state) {
  return { ...state, optimizationDate: null }
}

export default createReducer(DEFAULT_STATE, {
  [Types.SET_RISK_TARGET]: handleSetRiskTarget,
  [Types.SET_CONSTRAINTS]: handleSetConstraints,
  [Types.SET_SETTINGS]: handleSetSettings,
  [Types.REMOVE_FACTOR]: handleRemoveFactor,
  [Types.TOGGLE_TRADES_PANEL]: handleToggleTradesPanel,
  [LoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
  [Types.RESET_SETTINGS]: handleResetSettings,
  [Types.SET_CONSTRAINT]: handleSetConstraint,
  [Types.SET_SETTING]: handleSetSetting,
  [Types.SET_FACTORS]: handleSetFactors,
  [Types.REMOVE_ACTIVATED_FACTOR]: handleRemoveActivatedFactor,
  [Types.LOAD_OPTIMIZATION_CONFIG]: handleLoadOptimizationConfig,
  [Types.LOAD_SECURITIES_CONSTRAINTS]: handleLoadSecuritiesConstraints,
  [Types.RELOAD_CONSTRAINTS]: handleReloadConstraints,
  [Types.SET_OPTIMIZATION_DATE]: handleSetOptimizationDate,
  [PortfolioRootTypes.SET_PERIOD]: handlePortfolioSetPeriod,
})

function* handleLoadExperiment({ experiment }) {
  const { forecasts } = yield select(state => ({
    forecasts: getForecasts(state),
  }))

  const forecastId = get(experiment, "config.constraints.settings.forecast")
  if (forecastId) {
    const forecastExists = some(forecasts, elem => elem.id === forecastId)
    if (!forecastExists) {
      experiment = omit(experiment, "config.constraints.settings.forecast")
      yield put(notificationCreators.add("error", null, "The saved forecast on the experiment does not exist. The default forecast, Implied Returns, has been selected."))
    }
  }

  yield put(Creators.loadOptimizationConfig(experiment))
}

function* handleLoadForecast({ resource }) {
  if (resource === "optimizationConstraintsPreferences") {
    const { selectedCustomForecast, storedForecastId, settings } = yield select(state => ({
      selectedCustomForecast: getSelectedCustomForecast(state),
      storedForecastId: getForecastConstraint(state),
      settings: getCurrentSettings(state),
    }))

    // If forecast constraint isn't in forecasts, show notification and update user preference
    if (storedForecastId && !selectedCustomForecast) {
      yield put(notificationCreators.add("info", null, "The previously selected forecast could not be loaded."))
      yield put(Creators.setSettings(omit(settings, "forecast")))
    }
  }
}

export function* saga() {
  yield takeLatest(Types.SET_OPTIMIZATION_DATE, handleSetSingleDate)
  yield takeLatest(Types.LOAD_EXPERIMENT, handleLoadExperiment)
  yield takeLatest(LoaderTypes.LOAD_SUCCESS, handleLoadForecast)
}
